import * as React from 'react';
import { Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  dashboardTableSelectTriggerClassName,
  dashboardTableTextActionClassName,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import {
  AutocompleteFilter,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shigo-ui/ui';

import { formatDisplayDate } from '../../../../../lib/format';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useAssetTagsQuery, useAssetsQuery } from '../../hooks';
import {
  A2vAssetType,
  AssetsSortDirection,
  AssetsSortProperty,
  AssetsTypeFilter,
  defaultAssetsFilter,
  type A2vAsset,
} from '../../model';
import { useAssetsRouteFilters } from '../../useAssetsRouteFilters';
import { AssetEditDialog } from '../AssetEditDialog';
import { AssetUploadDialog } from '../AssetUploadDialog';
import { AssetActions } from './AssetActions';
import { AssetPreviewCell } from './AssetPreviewCell';

const assetDateFormat = { day: '2-digit', month: '2-digit', year: 'numeric' } as const;

export function AssetsTable() {
  const { can } = useShigoPermissions();
  const canUpload = can(ShigoPermission.A2vAssetsUpload);
  const canEdit = can(ShigoPermission.A2vAssetsEdit);
  const { filters, setAssetType, setPagination, setSearch, setSort, setTags } =
    useAssetsRouteFilters();
  const query = useAssetsQuery(filters);
  const [searchDraft, setSearchDraft] = React.useState({
    source: filters.searchTerm,
    value: filters.searchTerm,
  });
  const searchValue =
    searchDraft.source === filters.searchTerm ? searchDraft.value : filters.searchTerm;
  const [tagSearch, setTagSearch] = React.useState('');
  const tagsQuery = useAssetTagsQuery(tagSearch);
  const tagOptions = Array.from(new Set([...filters.tags, ...(tagsQuery.data ?? [])])).map(
    (tag) => ({ id: tag, label: tag }),
  );
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [editingAsset, setEditingAsset] = React.useState<A2vAsset | null>(null);

  React.useEffect(() => {
    if (searchValue === filters.searchTerm) return;
    const timeoutId = window.setTimeout(() => {
      setSearch(searchValue);
    }, 650);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [filters.searchTerm, searchValue, setSearch]);

  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<A2vAsset>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('a2v.assets.name'),
          headerClassName: 'w-[30%] pl-4',
          className: 'w-[30%] pl-4',
          sortable: true,
          sortKey: 'name',
          cell: (asset) => (
            <div className="flex min-w-0 items-center gap-3">
              <AssetPreviewCell asset={asset} />
              {canEdit ? (
                <button
                  type="button"
                  className={dashboardTableTextActionClassName()}
                  onClick={() => {
                    setEditingAsset(asset);
                  }}
                >
                  {asset.name}
                </button>
              ) : (
                <span className="truncate font-medium">{asset.name}</span>
              )}
            </div>
          ),
        },
        {
          id: 'assetType',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('a2v.assets.type'),
          sortable: true,
          sortKey: 'assetType',
          className: 'w-24',
          headerClassName: 'w-24',
          value: (asset) => dashboardT(`a2v.assets.types.${asset.assetType}`),
        },
        {
          id: 'size',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('a2v.assets.size'),
          className: 'w-28 whitespace-nowrap',
          headerClassName: 'w-28',
          value: (asset) => `${String(asset.width)} x ${String(asset.height)}`,
        },
        {
          id: 'tags',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('a2v.assets.tags'),
          cell: (asset) => {
            if (!asset.tags.length) return <span>-</span>;
            const label = asset.tags.join(', ');
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="block max-w-48 truncate">{label}</span>
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
              </Tooltip>
            );
          },
        },
        {
          id: 'createdOn',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('a2v.assets.created'),
          sortable: true,
          sortKey: 'createdOn',
          className: 'w-32 whitespace-nowrap',
          headerClassName: 'w-32',
          value: (asset) => formatDisplayDate(asset.createdOn, assetDateFormat),
        },
        {
          id: 'updatedOn',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('a2v.assets.updated'),
          sortable: true,
          sortKey: 'updatedOn',
          className: 'w-32 whitespace-nowrap',
          headerClassName: 'w-32',
          value: (asset) => formatDisplayDate(asset.updatedOn, assetDateFormat),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          hidden: !canEdit,
          cell: (asset) => <AssetActions asset={asset} onEdit={setEditingAsset} />,
        },
      ]),
    [canEdit],
  );

  return (
    <>
      <DashboardDataTable
        title={dashboardT('a2v.assets.title')}
        tableLabel={dashboardT('a2v.assets.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(asset) => asset.id}
        loading={query.isLoading || query.isFetching}
        loadingMessage={dashboardT('a2v.assets.loading')}
        emptyMessage={dashboardT('a2v.assets.empty')}
        errorMessage={query.isError ? dashboardT('a2v.assets.loadError') : undefined}
        minWidthClassName="min-w-[980px]"
        pagination={{
          value: {
            page: filters.page,
            pageSize: filters.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: [5, 10, 25, 50, 100],
          disabled: query.isFetching,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
        sort={{
          value: filters.sortProperty
            ? {
                key: filters.sortProperty,
                direction:
                  filters.sortDirection === AssetsSortDirection.Asc
                    ? DashboardDataTableSortDirection.Asc
                    : DashboardDataTableSortDirection.Desc,
              }
            : undefined,
          onChange: (next) => {
            if (!next) {
              setSort(null, filters.sortDirection);
              return;
            }
            setSort(
              resolveAssetsSortProperty(next.key),
              next.direction === DashboardDataTableSortDirection.Asc
                ? AssetsSortDirection.Asc
                : AssetsSortDirection.Desc,
            );
          },
        }}
        toolbar={
          <>
            <DashboardTableSearchInput
              value={searchValue}
              aria-label={dashboardT('a2v.assets.search')}
              placeholder={dashboardT('a2v.assets.search')}
              onChange={(event) => {
                setSearchDraft({ source: filters.searchTerm, value: event.target.value });
              }}
            />
            <Select
              value={filters.assetType ?? AssetsTypeFilter.All}
              onValueChange={(value: AssetsTypeFilter) => {
                setAssetType(resolveAssetTypeFilter(value));
              }}
            >
              <SelectTrigger
                className={dashboardTableSelectTriggerClassName()}
                aria-label={dashboardT('a2v.assets.typeFilter')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={AssetsTypeFilter.All}>{dashboardT('a2v.assets.all')}</SelectItem>
                <SelectItem value={AssetsTypeFilter.Image}>
                  {dashboardT('a2v.assets.types.IMAGE')}
                </SelectItem>
                <SelectItem value={AssetsTypeFilter.Video}>
                  {dashboardT('a2v.assets.types.VIDEO')}
                </SelectItem>
              </SelectContent>
            </Select>
            <AutocompleteFilter
              label={dashboardT('a2v.assets.tagsFilter')}
              selectedIds={filters.tags}
              options={tagOptions}
              onSelectedIdsChange={setTags}
              onSearchTermChange={setTagSearch}
              searchDebounceMs={400}
              loading={tagsQuery.isFetching}
              placeholder={dashboardT('a2v.assets.tagsFilter')}
              emptyMessage={dashboardT('a2v.assets.noTags')}
              className="bg-card w-[220px] max-w-full"
            />
            {canUpload ? (
              <Button
                onClick={() => {
                  setUploadOpen(true);
                }}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                {dashboardT('a2v.assets.upload.action')}
              </Button>
            ) : null}
          </>
        }
      />
      {uploadOpen ? (
        <AssetUploadDialog
          onClose={() => {
            setUploadOpen(false);
          }}
        />
      ) : null}
      {editingAsset ? (
        <AssetEditDialog
          asset={editingAsset}
          onClose={() => {
            setEditingAsset(null);
          }}
        />
      ) : null}
    </>
  );
}

function resolveAssetTypeFilter(value: AssetsTypeFilter) {
  if (value === AssetsTypeFilter.Image) return A2vAssetType.Image;
  if (value === AssetsTypeFilter.Video) return A2vAssetType.Video;
  return null;
}

function resolveAssetsSortProperty(value: string | undefined) {
  return (
    Object.values(AssetsSortProperty).find((sortProperty) => sortProperty === value) ??
    defaultAssetsFilter.sortProperty
  );
}
